import React, { useMemo, useState, useCallback, useRef } from 'react';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import useTranslation from '../../../translations/use-translation';
import './CSCOwnerRequests.styles.scss';

// ── Status definitions ──────────────────────────────────────────────────────
// STATUSES → what the CSC owner can actively SET via the dropdown
const STATUSES = ['Applied', 'In Progress', 'Completed', 'Rejected'];
// FILTER_STATUSES → all statuses visible in the filter tabs (includes citizen-cancelled)
const FILTER_STATUSES = [...STATUSES, 'Cancelled'];
const STATUS_CLASS = {
  'Applied':     'is-applied',
  'In Progress': 'is-progress',
  'Completed':   'is-completed',
  'Rejected':    'is-rejected',
  'Cancelled':   'is-cancelled',
};

// ── localStorage helpers ────────────────────────────────────────────────────
const norm = (v) => String(v || '').trim().toLowerCase();

const getProfile = () => {
  try { return JSON.parse(localStorage.getItem('cscOwnerProfile')) || {}; }
  catch { return {}; }
};

const getAllApplications = () => {
  try { return JSON.parse(localStorage.getItem('userApplications') || '[]'); }
  catch { return []; }
};

// An application belongs to this owner's center if any reliable identifier
// matches: center id, center name, mobile, or email.
const belongsToOwner = (app, profile) => {
  const c = app.cscCenter || {};
  if (profile.id && c.cscCenterId && profile.id === c.cscCenterId) return true;
  if (profile.centerName && norm(c.cscCenterName) === norm(profile.centerName)) return true;
  if (profile.mobile && c.cscCenterMobile && profile.mobile === c.cscCenterMobile) return true;
  if (profile.email && norm(c.cscCenterEmail) === norm(profile.email)) return true;
  return false;
};

const fmtAmount = (a) => (Number(a) === 0 ? 'FREE' : `₹${a}`);

// ── Status colour map ───────────────────────────────────────────────────────
const STATUS_STYLE = {
  'Applied':     { bg: 'rgba(33,150,243,0.10)',  color: '#1565C0', border: 'rgba(33,150,243,0.30)' },
  'In Progress': { bg: 'rgba(255,152,0,0.10)',   color: '#E65100', border: 'rgba(255,152,0,0.30)' },
  'Completed':   { bg: 'rgba(56,142,60,0.10)',   color: '#2E7D32', border: 'rgba(56,142,60,0.30)' },
  'Rejected':    { bg: 'rgba(211,47,47,0.10)',   color: '#C62828', border: 'rgba(211,47,47,0.30)' },
  'Cancelled':   { bg: 'rgba(120,120,120,0.10)', color: '#555',    border: 'rgba(120,120,120,0.30)' },
};

const StatusSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const cur = STATUS_STYLE[value] || STATUS_STYLE['Applied'];
  const isCancelled = value === 'Cancelled';

  const handleSelect = (s) => { onChange(s); setOpen(false); };

  // Citizen-cancelled — show read-only badge, no dropdown
  if (isCancelled) {
    return (
      <div className="status-select status-select--readonly">
        <span
          className="status-select__trigger status-select__trigger--readonly"
          style={{ background: cur.bg, color: cur.color, borderColor: cur.border }}
          title="Cancelled by citizen — cannot be changed"
        >
          <span className="status-select__dot" style={{ background: cur.color }} />
          <span>Cancelled by Citizen</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className="status-select"
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
      tabIndex={-1}
    >
      <button
        type="button"
        className="status-select__trigger"
        style={{ background: cur.bg, color: cur.color, borderColor: cur.border }}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="status-select__dot" style={{ background: cur.color }} />
        <span>{value}</span>
        <span className={`status-select__arrow${open ? ' status-select__arrow--open' : ''}`}>▾</span>
      </button>

      {open && (
        <ul className="status-select__menu" role="listbox">
          {STATUSES.map((s) => {
            const st = STATUS_STYLE[s] || {};
            return (
              <li
                key={s}
                role="option"
                aria-selected={s === value}
                className={`status-select__option${s === value ? ' status-select__option--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleSelect(s); }}
              >
                <span className="status-select__badge" style={{ background: st.bg, color: st.color, borderColor: st.border }}>
                  <span className="status-select__dot" style={{ background: st.color }} />
                  {s}
                </span>
                {s === value && <span className="status-select__check">✓</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Uploaded docs may be plain name strings (older/name-only) or rich objects
// { name, fileName, fileType, content }. Normalise to an object shape.
const normalizeDoc = (d) => (typeof d === 'string' ? { name: d } : (d || {}));

const downloadDoc = (doc) => {
  if (!doc?.content) return;
  const a = document.createElement('a');
  a.href = doc.content;
  a.download = doc.fileName || doc.name || 'document';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const CSCOwnerRequests = () => {
  const translate = useTranslation();
  const profile = useMemo(getProfile, []);

  const realRequests = useMemo(
    () => getAllApplications().filter((app) => belongsToOwner(app, profile)),
    [profile]
  );

  const [requests, setRequests] = useState(() => realRequests);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null); // request opened in the detail drawer

  const updateOwnerDocs = useCallback((orderId, newOwnerDocs) => {
    const patch = (r) => ({ ...r, ownerDocuments: newOwnerDocs });
    const all = getAllApplications().map((app) => (app.orderId === orderId ? patch(app) : app));
    try { localStorage.setItem('userApplications', JSON.stringify(all)); } catch { /* full */ }
    setRequests(all.filter((app) => belongsToOwner(app, profile)));
    setActive((prev) => (prev && prev.orderId === orderId ? patch(prev) : prev));
  }, [profile]);

  const updateStatus = useCallback((orderId, newStatus) => {
    const now = new Date().toISOString();
    const today = new Date().toLocaleDateString('en-GB'); // dd/mm/yyyy
    const patch = (r) => ({
      ...r,
      serviceStatus: newStatus,
      lastUpdated: today,
      applicationLastUpdatedDate: today,
      statusHistory: [
        ...(r.statusHistory || []),
        {
          status: newStatus,
          timestamp: now,
          updatedBy: profile.centerName || 'CSC Owner',
          comment: '',
        },
      ],
    });

    const all = getAllApplications().map((app) => (app.orderId === orderId ? patch(app) : app));
    try { localStorage.setItem('userApplications', JSON.stringify(all)); } catch { /* full */ }
    setRequests(all.filter((app) => belongsToOwner(app, profile)));
    setActive((prev) => (prev && prev.orderId === orderId ? patch(prev) : prev));
  }, [profile]);

  // Counts per status (for the filter chips) — includes Cancelled
  const counts = useMemo(() => {
    const c = { All: requests.length };
    FILTER_STATUSES.forEach((s) => { c[s] = requests.filter((r) => r.serviceStatus === s).length; });
    return c;
  }, [requests]);

  const visible = useMemo(() => {
    const q = norm(search);
    return requests.filter((r) => {
      if (statusFilter !== 'All' && r.serviceStatus !== statusFilter) return false;
      if (!q) return true;
      return (
        norm(r.serviceName).includes(q) ||
        norm(r.applicant?.name).includes(q) ||
        norm(r.applicant?.email).includes(q)
      );
    });
  }, [requests, statusFilter, search]);

  const hasAny = requests.length > 0;

  return (
    <div className="cop-requests">
      <div className="cop-requests__header">
        <h2 className="cop-requests__title">{translate('CSC_REQUESTS_HEADING')}</h2>
        <p className="cop-requests__subtitle">{translate('CSC_REQUESTS_SUBTITLE')}</p>
      </div>

      {!hasAny ? (
        <div className="cop-requests__empty">
          <span className="cop-requests__empty-icon"><InboxOutlinedIcon /></span>
          <p className="cop-requests__empty-text">{translate('CSC_REQUESTS_EMPTY')}</p>
        </div>
      ) : (
        <>
          {/* Filter + search toolbar */}
          <div className="cop-requests__toolbar">
            <div className="cop-requests__tabs">
              {['All', ...FILTER_STATUSES].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cop-requests__tab${statusFilter === s ? ' cop-requests__tab--active' : ''}`}
                  onClick={() => setStatusFilter(s)}
                >
                  {s === 'All' ? translate('CSC_REQUESTS_ALL') : s}
                  <span className="cop-requests__tab-count">{counts[s] ?? 0}</span>
                </button>
              ))}
            </div>

            <div className="cop-requests__search">
              <SearchIcon className="cop-requests__search-icon" />
              <input
                className="cop-requests__search-input"
                placeholder={translate('CSC_REQUESTS_SEARCH')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Request cards */}
          {visible.length === 0 ? (
            <div className="cop-requests__empty cop-requests__empty--compact">
              <span className="cop-requests__empty-icon"><InboxOutlinedIcon /></span>
              <p className="cop-requests__empty-text">{translate('CSC_REQUESTS_NO_MATCH')}</p>
            </div>
          ) : (
            <div className="cop-requests__list">
              {visible.map((r) => {
                const applicantName = r.applicant?.name || translate('CSC_REQUESTS_APPLICANT');
                const initial = (applicantName[0] || 'U').toUpperCase();
                const docs = r.uploadedDocuments || [];
                return (
                  <div
                    className="cop-req-card"
                    key={r.orderId}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActive(r)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActive(r)}
                  >
                    {/* Top: applicant + status */}
                    <div className="cop-req-card__top">
                      <div className="cop-req-card__applicant">
                        <span className="cop-req-card__avatar">{initial}</span>
                        <div className="cop-req-card__who">
                          <span className="cop-req-card__name">
                            <PersonOutlineIcon className="cop-req-card__name-icon" />
                            {applicantName}
                          </span>
                          {r.applicant?.email && (
                            <span className="cop-req-card__email">{r.applicant.email}</span>
                          )}
                        </div>
                      </div>
                      <span className={`cop-req-card__status ${STATUS_CLASS[r.serviceStatus] || 'is-applied'}`}>
                        {r.serviceStatus}
                      </span>
                    </div>

                    {/* Service */}
                    <div className="cop-req-card__service">
                      <DescriptionOutlinedIcon className="cop-req-card__service-icon" />
                      <span className="cop-req-card__service-name">{r.serviceName}</span>
                      <span className="cop-req-card__amount">{fmtAmount(r.amount)}</span>
                    </div>

                    {/* Meta row */}
                    <div className="cop-req-card__meta">
                      <span className="cop-req-card__meta-item">
                        <EventOutlinedIcon />
                        {translate('CSC_REQUESTS_SUBMITTED')}: {r.applicationSubmissionDate || r.lastUpdated || '—'}
                      </span>
                      {r.serviceLastDate && (
                        <span className="cop-req-card__meta-item">
                          <AccessTimeOutlinedIcon />
                          {translate('CSC_REQUESTS_LAST_DATE')}: {r.serviceLastDate}
                        </span>
                      )}
                    </div>

                    {/* Documents */}
                    {docs.length > 0 && (
                      <div className="cop-req-card__docs">
                        <span className="cop-req-card__docs-label">
                          {translate('CSC_REQUESTS_DOCS')} ({docs.length})
                        </span>
                        <div className="cop-req-card__docs-chips">
                          {docs.map((d, i) => (
                            <span className="cop-req-card__doc-chip" key={i}>
                              <CheckCircleOutlineIcon />
                              {normalizeDoc(d).name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Status update */}
                    <div className="cop-req-card__actions" onClick={(e) => e.stopPropagation()}>
                      <span className="cop-req-card__actions-label">
                        {translate('CSC_REQUESTS_UPDATE_STATUS')}
                      </span>
                      <StatusSelect
                        value={r.serviceStatus}
                        onChange={(s) => updateStatus(r.orderId, s)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Detail drawer ──────────────────────────────────────────────────── */}
      {active && (
        <RequestDrawer
          request={active}
          translate={translate}
          onClose={() => setActive(null)}
          onStatusChange={(status) => updateStatus(active.orderId, status)}
          onOwnerDocUpdate={(docs) => updateOwnerDocs(active.orderId, docs)}
        />
      )}
    </div>
  );
};

// ── Detail drawer ─────────────────────────────────────────────────────────────
const RequestDrawer = ({ request: r, translate, onClose, onStatusChange, onOwnerDocUpdate }) => {
  const applicantName = r.applicant?.name || translate('CSC_REQUESTS_APPLICANT');
  const initial = (applicantName[0] || 'U').toUpperCase();
  const docs = r.uploadedDocuments || [];
  const ownerDocs = r.ownerDocuments || [];
  const c = r.cscCenter || {};
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const ownerFileRef = useRef(null);

  const handleOwnerDocUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                name: file.name,
                fileName: file.name,
                fileType: file.type,
                content: reader.result,
                uploadedAt: new Date().toISOString(),
              });
            reader.readAsDataURL(file);
          })
      )
    ).then((newDocs) => {
      onOwnerDocUpdate([...ownerDocs, ...newDocs]);
      setUploading(false);
    });
    e.target.value = '';
  };

  const deleteOwnerDoc = (idx) => {
    onOwnerDocUpdate(ownerDocs.filter((_, i) => i !== idx));
  };

  return (
    <>
      <div className="cop-req-drawer__overlay" onClick={onClose} aria-hidden="true" />
      <aside className="cop-req-drawer" role="dialog" aria-modal="true" aria-label="Request details">
        {/* Header */}
        <div className="cop-req-drawer__header">
          <div className="cop-req-drawer__header-id">
            <span className="cop-req-drawer__header-label">Request</span>
            <span className="cop-req-drawer__header-order">#{r.orderId}</span>
          </div>
          <button className="cop-req-drawer__close" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="cop-req-drawer__body">
          {/* Applicant */}
          <div className="cop-req-drawer__applicant">
            <span className="cop-req-drawer__avatar">{initial}</span>
            <div className="cop-req-drawer__applicant-info">
              <span className="cop-req-drawer__applicant-name">{applicantName}</span>
              <span className={`cop-req-card__status ${STATUS_CLASS[r.serviceStatus] || 'is-applied'}`}>
                {r.serviceStatus}
              </span>
            </div>
          </div>

          {/* Applicant contact */}
          <div className="cop-req-drawer__contact">
            {r.applicant?.email && (
              <a className="cop-req-drawer__contact-row" href={`mailto:${r.applicant.email}`}>
                <EmailOutlinedIcon /> {r.applicant.email}
              </a>
            )}
            {r.applicant?.mobile && (
              <a className="cop-req-drawer__contact-row" href={`tel:${r.applicant.mobile}`}>
                <PhoneOutlinedIcon /> {r.applicant.mobile}
              </a>
            )}
            {!r.applicant?.email && !r.applicant?.mobile && (
              <span className="cop-req-drawer__contact-row cop-req-drawer__contact-row--muted">
                <PersonOutlineIcon /> No applicant contact captured for this request
              </span>
            )}
          </div>

          {/* Service section */}
          <section className="cop-req-drawer__section">
            <h4 className="cop-req-drawer__section-title">Service</h4>
            <div className="cop-req-drawer__service">
              <DescriptionOutlinedIcon className="cop-req-drawer__service-icon" />
              <span className="cop-req-drawer__service-name">{r.serviceName}</span>
            </div>
            <div className="cop-req-drawer__rows">
              <div className="cop-req-drawer__row">
                <span className="cop-req-drawer__row-label"><LocalOfferOutlinedIcon /> Service charge</span>
                <span className="cop-req-drawer__row-value cop-req-drawer__row-value--amount">{fmtAmount(r.amount)}</span>
              </div>
              <div className="cop-req-drawer__row">
                <span className="cop-req-drawer__row-label"><EventOutlinedIcon /> {translate('CSC_REQUESTS_SUBMITTED')}</span>
                <span className="cop-req-drawer__row-value">{r.applicationSubmissionDate || r.lastUpdated || '—'}</span>
              </div>
              {r.serviceLastDate && (
                <div className="cop-req-drawer__row">
                  <span className="cop-req-drawer__row-label"><AccessTimeOutlinedIcon /> {translate('CSC_REQUESTS_LAST_DATE')}</span>
                  <span className="cop-req-drawer__row-value">{r.serviceLastDate}</span>
                </div>
              )}
              {r.applicationLastUpdatedDate && (
                <div className="cop-req-drawer__row">
                  <span className="cop-req-drawer__row-label"><UpdateOutlinedIcon /> Last updated</span>
                  <span className="cop-req-drawer__row-value">{r.applicationLastUpdatedDate}</span>
                </div>
              )}
            </div>
          </section>

          {/* Documents */}
          <section className="cop-req-drawer__section">
            <h4 className="cop-req-drawer__section-title">
              {translate('CSC_REQUESTS_DOCS')} ({docs.length})
            </h4>
            {docs.length > 0 ? (
              <ul className="cop-req-drawer__docs">
                {docs.map((raw, i) => {
                  const d = normalizeDoc(raw);
                  const hasFile = !!d.content;
                  return (
                    <li className="cop-req-drawer__doc" key={i}>
                      <span className="cop-req-drawer__doc-info">
                        <CheckCircleOutlineIcon />
                        <span className="cop-req-drawer__doc-text">
                          <span className="cop-req-drawer__doc-name">{d.name}</span>
                          {d.fileName && d.fileName !== d.name && (
                            <span className="cop-req-drawer__doc-file">{d.fileName}</span>
                          )}
                        </span>
                      </span>
                      {hasFile ? (
                        <span className="cop-req-drawer__doc-actions">
                          <button type="button" title="View" aria-label={`View ${d.name}`} onClick={() => setPreview(d)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </button>
                          <button type="button" title="Download" aria-label={`Download ${d.name}`} onClick={() => downloadDoc(d)}>
                            <DownloadOutlinedIcon fontSize="small" />
                          </button>
                        </span>
                      ) : (
                        <span className="cop-req-drawer__doc-na">name only</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="cop-req-drawer__empty-note">No documents were uploaded with this application.</p>
            )}
          </section>

          {/* Completion documents — only when status is Completed */}
          {r.serviceStatus === 'Completed' && (
            <section className="cop-req-drawer__section cop-req-drawer__section--completion">
              <h4 className="cop-req-drawer__section-title">
                <TaskAltOutlinedIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Completion Documents
              </h4>
              <p className="cop-req-drawer__section-hint">
                Upload the application number, receipt, or any completion documents for the applicant.
              </p>

              {ownerDocs.length > 0 && (
                <ul className="cop-req-drawer__docs">
                  {ownerDocs.map((raw, i) => {
                    const d = normalizeDoc(raw);
                    const hasFile = !!d.content;
                    return (
                      <li className="cop-req-drawer__doc" key={i}>
                        <span className="cop-req-drawer__doc-info">
                          <CheckCircleOutlineIcon />
                          <span className="cop-req-drawer__doc-text">
                            <span className="cop-req-drawer__doc-name">{d.name}</span>
                            {d.uploadedAt && (
                              <span className="cop-req-drawer__doc-file">
                                {new Date(d.uploadedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="cop-req-drawer__doc-actions">
                          {hasFile && (
                            <>
                              <button type="button" title="View" aria-label={`View ${d.name}`} onClick={() => setPreview(d)}>
                                <VisibilityOutlinedIcon fontSize="small" />
                              </button>
                              <button type="button" title="Download" aria-label={`Download ${d.name}`} onClick={() => downloadDoc(d)}>
                                <DownloadOutlinedIcon fontSize="small" />
                              </button>
                            </>
                          )}
                          <button type="button" title="Remove" aria-label={`Remove ${d.name}`} className="cop-req-drawer__doc-del" onClick={() => deleteOwnerDoc(i)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}

              <input
                ref={ownerFileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleOwnerDocUpload}
              />
              <button
                type="button"
                className="cop-req-drawer__upload-btn"
                onClick={() => ownerFileRef.current?.click()}
                disabled={uploading}
              >
                <UploadFileOutlinedIcon fontSize="small" />
                {uploading ? 'Uploading…' : 'Upload Documents'}
              </button>
            </section>
          )}

          {/* CSC center */}
          {(c.cscCenterName || c.cscCenterAddress) && (
            <section className="cop-req-drawer__section">
              <h4 className="cop-req-drawer__section-title">Applied at</h4>
              <div className="cop-req-drawer__center">
                <StorefrontOutlinedIcon className="cop-req-drawer__center-icon" />
                <div>
                  {c.cscCenterName && <p className="cop-req-drawer__center-name">{c.cscCenterName}</p>}
                  {c.cscCenterAddress && (
                    <p className="cop-req-drawer__center-addr">
                      <LocationOnOutlinedIcon /> {c.cscCenterAddress}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer: status control */}
        <div className="cop-req-drawer__footer">
          <span className="cop-req-drawer__footer-label">
            {translate('CSC_REQUESTS_UPDATE_STATUS')}
          </span>
          <StatusSelect
            value={r.serviceStatus}
            onChange={onStatusChange}
          />
        </div>
      </aside>

      {/* Document preview */}
      {preview && (
        <div className="cop-doc-preview" onClick={() => setPreview(null)}>
          <div className="cop-doc-preview__panel" onClick={(e) => e.stopPropagation()}>
            <div className="cop-doc-preview__head">
              <span className="cop-doc-preview__name">{preview.name}</span>
              <div className="cop-doc-preview__head-actions">
                <button type="button" title="Download" onClick={() => downloadDoc(preview)}>
                  <DownloadOutlinedIcon fontSize="small" />
                </button>
                <button type="button" title="Close" onClick={() => setPreview(null)}>
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            </div>
            <div className="cop-doc-preview__body">
              {preview.fileType?.startsWith('image/') ? (
                <img src={preview.content} alt={preview.name} className="cop-doc-preview__img" />
              ) : (
                <iframe src={preview.content} title={preview.name} className="cop-doc-preview__iframe" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CSCOwnerRequests;
